import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout from '../components/LegalPageLayout';
import { useLocale } from '../contexts/LocaleContext';

const CONTACT = 'contact@adfini.ai';

export default function TermsOfService() {
  const { locale } = useLocale();
  const lastUpdated = 'April 30, 2026';

  return (
    <LegalPageLayout title="Terms of Service" locale={locale}>
      <p className="text-[var(--studio-ink)]">Last updated: {lastUpdated}</p>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Agreement</h2>
        <p>
          These Terms of Service (“Terms”) govern your access to and use of Adfini’s websites,
          applications, and related services (the “Service”) operated by Adfini (“we”, “us”). By
          creating an account, clicking to accept, or using the Service, you agree to these Terms.
          If you do not agree, do not use the Service.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Eligibility</h2>
        <p>
          You must be able to form a binding contract in your jurisdiction and meet any minimum age
          requirements (typically 18+, or the age of majority where you live). You may not use the
          Service if you are prohibited from receiving services under applicable law.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Accounts</h2>
        <p>
          You are responsible for your account credentials and for all activity under your account.
          You must provide accurate information and keep it updated. Notify us at{' '}
          <a className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" href={`mailto:${CONTACT}`}>
            {CONTACT}
          </a>{' '}
          if you suspect unauthorized access.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">The Service and AI-generated content</h2>
        <p>
          The Service helps you create visual and related assets using automated and AI-assisted
          tools. Outputs may be imperfect or unsuitable for a particular use. You are responsible
          for reviewing outputs before use, for compliance with laws and third-party rights (for
          example trademarks, likeness, and copyright in materials you upload or in generated
          results), and for how you publish or commercialize outputs. We do not guarantee specific
          results, timelines, or uninterrupted availability.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Credits and payments</h2>
        <p>
          The Service may use a credit system. Credits are a limited license to use features of the
          Service, not a stored-value instrument or currency. Pricing, pack sizes, and credit costs
          per workflow may change; we will use reasonable efforts to communicate material changes
          where appropriate.
        </p>
        <p>
          Paid purchases are processed by our payment provider. You authorize us and our providers to
          charge your selected payment method. <strong className="text-[var(--studio-ink)]">Fees are generally
          non-refundable</strong> except as described in our{' '}
          <Link className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" to="/refund-policy">
            Refund policy
          </Link>{' '}
          or where required by law.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Acceptable use</h2>
        <p>You agree not to:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Violate law or infringe others’ rights</li>
          <li>Upload malware, attempt to break or overload the Service, or probe systems without authorization</li>
          <li>Scrape, resell, or sublicense the Service except as expressly allowed</li>
          <li>Use the Service to generate unlawful, harassing, or exploitative content</li>
          <li>Circumvent credit, billing, or access controls</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Your content</h2>
        <p>
          You retain ownership of content you upload. You grant us a license to host, process,
          transmit, and display that content as needed to operate and improve the Service (including
          using service providers). You represent you have the rights needed to grant this license.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Our intellectual property</h2>
        <p>
          The Service, branding, and our software are owned by us or our licensors. Except for the
          limited right to use the Service under these Terms, no rights are granted to you.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Privacy</h2>
        <p>
          Our{' '}
          <Link className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" to="/privacy">
            Privacy policy
          </Link>{' '}
          explains how we collect and use personal data.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Disclaimer</h2>
        <p>
          THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY
          LAW, WE DISCLAIM IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
          AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE OR UNINTERRUPTED.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Limitation of liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
          SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL,
          ARISING FROM YOUR USE OF THE SERVICE. OUR AGGREGATE LIABILITY FOR CLAIMS RELATING TO THE
          SERVICE WILL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID TO US FOR THE SERVICE IN THE
          TWELVE (12) MONTHS BEFORE THE CLAIM OR (B) ONE HUNDRED US DOLLARS (US$100), EXCEPT WHERE
          LIABILITY CANNOT BE LIMITED UNDER APPLICABLE LAW.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Suspension and termination</h2>
        <p>
          We may suspend or terminate access to the Service for violation of these Terms, risk to the
          Service or others, or as required by law. You may stop using the Service at any time.
          Provisions that by their nature should survive will survive termination.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Changes</h2>
        <p>
          We may modify the Service or these Terms. If we make material changes, we will take
          reasonable steps to notify you (for example, by posting an updated date or, where
          appropriate, by email). Continued use after changes become effective constitutes acceptance
          of the updated Terms, except where applicable law requires a different process.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Governing law</h2>
        <p>
          These Terms are governed by the laws of the jurisdiction in which Adfini operates, without
          regard to conflict-of-law rules, except where mandatory consumer protections in your country
          require otherwise.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-medium text-[var(--studio-ink)]">Contact</h2>
        <p>
          Questions about these Terms:{' '}
          <a className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" href={`mailto:${CONTACT}`}>
            {CONTACT}
          </a>
          . For account or support requests, see{' '}
          <Link className="text-[var(--studio-ink)] underline hover:text-[var(--studio-ink)]" to="/help">
            Help &amp; support
          </Link>
          .
        </p>
      </section>
    </LegalPageLayout>
  );
}
